/**
 * The drawer's two follow-up surfaces: what a submission is WAITING on, and
 * what a follow-up submission RECORDED.
 *
 * They are the two halves of the same question. On the parent, "has anybody
 * done the rest of this yet, and if not, what do I do about it". On the child,
 * "did the customer actually check what we told them" — which is the audit
 * question the whole confirm mode exists to answer, and which nothing has ever
 * rendered even though every submission has carried the record since the
 * handoff shipped.
 *
 * See docs/PLAYBOOK-workflows.md, phase W3.
 */
import { Link } from 'react-router-dom';
import type { ContinuesInfo, FollowUpSummary } from '../../api/forms';
import { formatFull } from './submissionColumns';
import { followUpCell } from './followUpCell';
import { ago } from './SsoShared';

const when = (iso: string | null | undefined) => (iso ? formatFull(iso) : '—');

/**
 * The parent's Follow-up tab.
 *
 * A status card of plain facts, then the house timeline. When the carrier
 * FAILED the timeline is replaced by the failure banner, because the state is
 * then a delivery problem rather than a waiting problem: nobody is late, an
 * email did not leave, and the action is to send it rather than to chase.
 */
export function FollowUpTabPanel({ followUp, onCopy, copied, onResend }: {
  followUp: FollowUpSummary;
  onCopy: () => void;
  copied: boolean;
  onResend: () => void;
}) {
  const cell = followUpCell(followUp);
  const inv = followUp.invite;

  return (
    <>
      <div className="fs-card" style={{ marginBottom: 16 }}>
        <div className="fs-card-head" style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 0 }}>
          <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: 'var(--ink-900)' }}>
            {followUp.invite?.formName || 'The follow-up form'}
          </div>
          {cell && <span className={`fs-pill ${cell.pill.intent}`}>{cell.pill.text}</span>}
        </div>
        <div className="fs-card-body" style={{ gap: 8 }}>
          <Fact label="Sent to">
            {inv?.email
              ? <>{inv.email}</>
              : <span style={{ color: 'var(--text-muted)' }}>nobody — the link was minted but not addressed</span>}
          </Fact>
          <Fact label="Link">
            {inv && !inv.revoked
              ? <>expires {when(inv.expiresAt)} · reusable until the follow-up is submitted, so forwarding it is fine</>
              : <span style={{ color: 'var(--text-muted)' }}>no live link</span>}
          </Fact>
          {followUp.child && (
            <Fact label="Arrived">
              <Link to={`/admin/forms/${followUp.child.formId}/submissions/${followUp.child.id}`}>
                {followUp.child.submissionId} →
              </Link>{' '}
              {when(followUp.child.createdAt)}
            </Fact>
          )}
        </div>
      </div>

      {followUp.failure ? (
        <div className="sl-fail">
          <div className="body">
            <b>The link never went out.</b>
            <code>{followUp.failure.message}</code>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
              {followUp.failure.connectionName} · last tried {when(followUp.failure.at)}
            </div>
          </div>
          <button className="fs-btn primary sm" onClick={onResend}>Re-send…</button>
        </div>
      ) : (
        <div className="sl-tl">
          {inv && (
            <Step title="Link sent" dot="delivered"
              who={`${when(inv.sentAt)}${inv.email ? ` · to ${inv.email}` : ''}`} />
          )}
          {followUp.skipped && !inv && (
            <Step title="Nothing was sent" dot=""
              who={`${followUp.skipped.connectionName} — ${followUp.skipped.reason}`} />
          )}
          {inv?.firstOpenedAt ? (
            <Step title="Opened" dot="edit"
              who={`${when(inv.firstOpenedAt)}${followUp.saved
                ? ` · ${followUp.saved.count !== null && followUp.saved.label
                  ? `${followUp.saved.count} ${followUp.saved.label}`
                  : 'work'} saved so far`
                : ''}`} />
          ) : inv ? (
            <Step title="Not opened yet" dot=""
              who={`sent ${ago(inv.sentAt)}`} />
          ) : null}
          {inv && inv.reminderCount > 0 && (
            <Step title={`Reminded ${inv.reminderCount === 1 ? 'once' : `${inv.reminderCount} times`}`} dot="delivered"
              who={`last ${when(inv.lastRemindedAt)} — the same link, not a new one`} />
          )}
          {inv?.nextReminderAt && !followUp.child && (
            <Step title="Reminder scheduled" dot="" future
              who={`${when(inv.nextReminderAt)} — re-sends the same link${
                inv.reminderCount === 0 ? ', then once more a few days later' : ', then we stop'}`} />
          )}
          {followUp.child && (
            <Step title="Completed" dot="delivered" who={when(followUp.child.createdAt)} />
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        {inv?.url && !followUp.child && (
          <button className="fs-btn neutral sm" onClick={onCopy}>{copied ? 'Copied' : 'Copy link'}</button>
        )}
        {inv && !followUp.child && (
          <button className="fs-btn neutral sm" onClick={onResend}>Re-send…</button>
        )}
      </div>
    </>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10, fontSize: 13, lineHeight: 1.5 }}>
      <span style={{ flex: 'none', width: 74, color: 'var(--text-muted)', fontWeight: 600 }}>{label}</span>
      <span style={{ flex: 1, color: 'var(--ink-800)', wordBreak: 'break-word' }}>{children}</span>
    </div>
  );
}

function Step({ title, who, dot, future }: { title: string; who: string; dot: string; future?: boolean }) {
  return (
    <div className="sl-tl-item" style={future ? { opacity: 0.65 } : undefined}>
      <span className={`sl-tl-dot ${dot}`} />
      <div className="sl-tl-title">{title}</div>
      <div className="sl-tl-who">{who}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The child's side
// ---------------------------------------------------------------------------

const text = (v: unknown) =>
  (v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));

/**
 * What the person was shown, and what they said about it.
 *
 * Every submission that continued another one has carried this record since
 * the handoff shipped and nothing rendered it, which made "the customer
 * confirmed the billing contact" a claim the business relied on and could not
 * look at.
 *
 * The awkward case is confirmed AND changed. Both facts are true and neither
 * cancels the other — they pressed the button and then edited the value — so
 * both are shown rather than one being picked as the winner.
 */
export function ConfirmationRecord({ continues, at }: { continues: ContinuesInfo; at: string }) {
  const rows = Object.entries(continues.carried || {});
  if (!rows.length) return null;

  return (
    <div className="fs-card" style={{ marginBottom: 16 }}>
      <div className="fs-card-head">
        <h2 style={{ fontSize: 15 }}>What they were shown, and what they said</h2>
      </div>
      <div className="fs-card-body" style={{ gap: 0 }}>
        {rows.map(([key, rec]) => {
          const confirmed = rec.confirmed === true;
          const corrected = 'corrected' in rec;
          const final = corrected ? rec.corrected : rec.from;
          const asked = 'confirmed' in rec;
          return (
            <div key={key} className="fu-record">
              <div>
                <div className="label">{continues.labels[key] || key}</div>
                <div className="value">{text(final) || <span style={{ color: 'var(--text-faint)' }}>blank</span>}</div>
              </div>
              <span className={`fs-pill ${corrected ? 'warn' : confirmed ? 'ok' : 'neutral'}`}>
                {corrected ? '✎ corrected' : confirmed ? '✓ confirmed' : '🔒 locked'}
              </span>
              <div className="says">
                {!asked && !corrected && 'Shown, not asked — re-applied on submit.'}
                {asked && !corrected && confirmed && `Shown “${text(rec.from)}” · said it was right · unchanged.`}
                {asked && !corrected && !confirmed && 'Shown, and they did not say whether it was right.'}
                {corrected && (
                  <div className="sl-diff">
                    <div className="rm">− shown: <code>{text(rec.from) || '(blank)'}</code></div>
                    <div className="add">+ their correction: <code>{text(rec.corrected) || '(blank)'}</code></div>
                  </div>
                )}
                {corrected && confirmed && (
                  <div className="fs-consequence warn" style={{ marginTop: 8 }}>
                    <b>Confirmed, then changed.</b> They said “{text(rec.from)}” was right, and also edited it.
                    Both facts are recorded — read the value as the answer and the verdict as their intent.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="fs-card-foot">
        Recorded when this was submitted, {formatFull(at)}. This is the answer to
        “did the customer actually check it?”.
      </div>
    </div>
  );
}

/** "follow-up · Continues BC-1041 — Demo — Order · Cascade Vet Clinic" */
export function ContinuesBanner({ continues }: { continues: ContinuesInfo }) {
  return (
    <div className="sl-identity">
      <span className="fs-pill identity">follow-up</span>
      <span>
        Continues{' '}
        <Link to={`/admin/forms/${continues.formId}/submissions/${continues.id}`}>
          {continues.submissionId} — {continues.formName}
        </Link>
        {continues.summary ? ` · ${continues.summary}` : ''}
      </span>
    </div>
  );
}
